import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabaseClient';
import WorkspaceSkeleton from '../components/WorkspaceSkeleton';

// A passenger should never download the driver workspace (maps, dispatch,
// liveness gate) and a driver should never download the passenger dashboard.
const UserDashboard = lazy(() => import('./UserDashboard'));
const DriverDashboard = lazy(() => import('./driver-dashboard/DriverDashboard'));
const DriverIdentityGate = lazy(() => import('../components/driver-identity/DriverIdentityGate'));
const RapidRideFocus = lazy(() => import('./RapidRideFocus'));

function DashboardChunkFallback() {
  return <WorkspaceSkeleton label="Loading your UniAir dashboard" />;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [rapidGate, setRapidGate] = useState({userId:null,loading:true,tripId:null});
  const isDriver = user?.user_type === 'driver';
  const finishRapid = useCallback(() => {
    if (user?.id) {
      try { window.sessionStorage.removeItem(`uniair:rapid-active:${user.id}`); }
      catch { /* Browser storage is optional. */ }
    }
    setRapidGate(previous => ({...previous,loading:false,tripId:null}));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || isDriver) return undefined;
    let mounted=true;
    const check = async () => {
      const {data,error} = await supabase.rpc('uniair_get_my_rapid_trip_focus_v1',{
        p_private_trip_id:null,
      });
      if (!mounted) return;
      // Failure is never interpreted as an arrival or a new trip. Do not
      // block the ordinary dashboard if this optional Ride Now gate fails.
      const liveTripId=!error && data?.ok &&
        ['en_route','arrived','in_progress'].includes(data.stage)
        ? data.private_trip_id:null;
      let completedTripId=null;
      if (!liveTripId) {
        // The completion worker changes trip status before passengers can
        // react. Recover the *same* in-session ride once, instead of
        // unmounting its focus screen and losing the post-trip rating modal.
        let remembered=null;
        try { remembered=window.sessionStorage.getItem(`uniair:rapid-active:${user.id}`); }
        catch { /* Optional storage. */ }
        if(remembered){
          const {data:rememberedTrip,error:rememberedError}=await supabase.rpc(
            'uniair_get_my_rapid_trip_focus_v1',{p_private_trip_id:remembered});
          if(!mounted)return;
          if(!rememberedError&&rememberedTrip?.ok &&
            ['completed','en_route','arrived','in_progress'].includes(rememberedTrip.stage)){
            completedTripId=rememberedTrip.private_trip_id;
          } else {
            try {window.sessionStorage.removeItem(`uniair:rapid-active:${user.id}`);}
            catch { /* Optional storage. */ }
          }
        }
      }
      setRapidGate(previous=>({
        userId:user.id,loading:false,
        tripId:liveTripId||completedTripId||
          (previous.userId===user.id?previous.tripId:null),
      }));
    };
    void check();
    const onVisible=() => {
      if (document.visibilityState==='visible') void check();
    };
    document.addEventListener('visibilitychange',onVisible);
    return () => {
      mounted=false;
      document.removeEventListener('visibilitychange',onVisible);
    };
  },[user?.id,isDriver]);

  if (!user) return null;

  const waitingForRapidGate=!isDriver &&
    (rapidGate.userId!==user.id || rapidGate.loading);
  if (waitingForRapidGate) return <DashboardChunkFallback />;

  return (
    <Suspense fallback={<DashboardChunkFallback />}>
      {isDriver ? (
        <DriverIdentityGate><DriverDashboard /></DriverIdentityGate>
      ) : rapidGate.tripId ? (
        <RapidRideFocus activeTripId={rapidGate.tripId} onFinished={finishRapid} />
      ) : (
        <UserDashboard />
      )}
    </Suspense>
  );
}
